import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IResource, IUserGuide } from './IResource';
import { take } from 'rxjs/internal/operators';


@Injectable()
export class ResourceService {
  static readonly MAX_FILE_SIZE = 10000000;
  static readonly ACCEPT_FILES = [
    'image/*',
    'text/csv',
    'application/pdf',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ];

  static readonly ACCEPT_EXTENSIONS = [
    '.jpg', '.jpeg', '.gif', '.bmp', '.png',
    '.pdf', '.html',
    '.doc', '.docx', '.txt', '.rtf',
    '.xls', '.xlsx', '.csv',
    '.ppt', '.pptx'
  ];

  private baseUrl = environment.baseUrl;
  resourceBaseUrl = environment.resourceBase;

  private _resourcesBehaviorSubject: BehaviorSubject<IResource[]> = new BehaviorSubject<IResource[]|undefined>([]);
  resources$: Observable<IResource[]> = this._resourcesBehaviorSubject.asObservable();

  private _userGuideBS: BehaviorSubject<IUserGuide[]> = new BehaviorSubject<IUserGuide[]>([]);
  userGuides$: Observable<IUserGuide[]> = this._userGuideBS.asObservable();

  constructor(private http: HttpClient) { }

  validateResourceName(strategyId: number, name: string): Observable<{isDuplicateResourceName: boolean}> {
    return this.http.get<{isDuplicateResourceName: boolean}>(`${this.baseUrl}/resources/strategies/${strategyId}/validate-resource-name`,
      this.createRequestOptions({name}));
  }

  getResources(strategyId: number) {
    this.http.get<IResource[]>(`${this.baseUrl}/resources/strategies/${strategyId}/evidences`).subscribe((newResources: IResource[]) => {
      this._resourcesBehaviorSubject.next(newResources);
    });
  }

  createLinkResource(strategyId: number, resource: IResource): Observable<IResource> {
    return this.http.post<IResource>(`${this.baseUrl}/resources/strategies/${strategyId}/upload-link`,
      { url: resource.resourcePath,
              resourceName: resource.name,
              resourceDescription: resource.description,
              evidenceType: resource.evidenceType});
  }

  editResource(resource: IResource): Observable<IResource> {
    return this.http.put<IResource>(`${this.baseUrl}/resources/${resource.resourceId}`, this.toEdit(resource));
  }

  deleteResource(resourceId: number): Observable<{}> {
    return this.http.delete<{}>(`${this.baseUrl}/resources/${resourceId}`);
  }

  getResourceUploadEvidenceUrl(strategyId: number) {
    return `${this.baseUrl}/resources/strategies/${strategyId}/upload-evidence`;
  }

  transformToResourceDownloadUrl(resource: IResource) {
    if (resource) {
      return `${this.resourceBaseUrl}/${resource.resourcePath}`;
    } else {
      return '/assets/images/exclamation.svg'; // TODO this represents a broken link effectively
    }
  }


  fetchUserGuides(): void {
    this.http.get<IUserGuide[]>(`${this.baseUrl}/resources/user-guides`)
      .pipe(take(1))
      .subscribe((userGuides: IUserGuide[]) => {
        this._userGuideBS.next(userGuides);
      });
  }

  private toEdit(resource: IResource) {
    if (resource.isUrl) {
      return {url: resource.resourcePath,
              resourceName: resource.name,
              resourceDescription: resource.description,
              evidenceType: resource.evidenceType};
    } else {
      return {resourceName: resource.name,
              resourceDescription: resource.description,
              evidenceType: resource.evidenceType};
    }
  }

  private createRequestOptions(paramsObj?: { [key: string]: string; }) {
    const params: HttpParams = paramsObj
      ? Object.keys(paramsObj).reduce(
        (previous, key) => previous.set(key, paramsObj[key]),
        new HttpParams({ encoder: new HttpUrlEncodingCodec()}))
      : undefined;
    return { params };
  }
}
